import { marginOf } from './margins.js';
import { fromCents, toCents } from './money.js';
export const BUDGET_KINDS = ['economico', 'tesoreria'];
export function monthOf(date) {
    return date.slice(0, 7);
}
function add(ledger, date, side, cents) {
    if (cents === 0)
        return;
    const key = monthOf(date);
    const row = ledger.get(key) ?? { in: 0, out: 0 };
    row[side] += cents;
    ledger.set(key, row);
}
function flows(ledger) {
    const months = [...ledger.keys()].sort();
    return {
        months,
        in: months.map((key) => fromCents(ledger.get(key).in)),
        out: months.map((key) => fromCents(ledger.get(key).out)),
    };
}
/** The part of a sum of the whole invoice that belongs to the commessa, to the cent. */
function shareOf(invoice, cents) {
    const taxable = toCents(invoice.taxable);
    return taxable === 0 ? 0 : Math.round((cents * toCents(invoice.amount)) / taxable);
}
/**
 * The money an invoice has really moved, by date: the advances of the bank
 * when credited; the payments of the client first repay the advance (the bank
 * keeps them), the rest reaches the company.
 */
function cashMoved(invoice) {
    const events = invoice.advances.map((item) => ({ date: item.date, cents: toCents(item.amount) }));
    let owed = events.reduce((sum, item) => sum + item.cents, 0);
    for (const item of [...invoice.settlements].sort((a, b) => a.date.localeCompare(b.date))) {
        const cents = toCents(item.amount);
        const kept = Math.max(0, Math.min(owed, cents));
        owed -= kept;
        if (cents - kept !== 0)
            events.push({ date: item.date, cents: cents - kept });
    }
    return events;
}
/** What an invoice will still move: its open due dates, less the advance still to repay. */
function cashExpected(invoice) {
    const advanced = invoice.advances.reduce((sum, item) => sum + toCents(item.amount), 0);
    const repaid = Math.min(advanced, invoice.settlements.reduce((sum, item) => sum + toCents(item.amount), 0));
    let owed = advanced - repaid;
    const events = [];
    for (const due of [...invoice.openDues].sort((a, b) => a.dueDate.localeCompare(b.dueDate))) {
        const cents = toCents(due.residual);
        const kept = Math.max(0, Math.min(owed, cents));
        owed -= kept;
        if (cents - kept !== 0)
            events.push({ date: due.dueDate, cents: cents - kept });
    }
    return events;
}
/** The VAT of an imponibile at a rate, in cents. */
const vatCents = (cents, rate) => Math.round((cents * Number(rate)) / 100);
/** What is still to invoice of a rata or a cost: never below zero, never past the amount. */
function remainder(amount, invoiced) {
    const total = toCents(amount);
    const done = toCents(invoiced);
    const sign = total < 0 ? -1 : 1;
    return sign * Math.max(0, sign * (total - done));
}
/** The budget of a commessa, economico and tesoreria, previsto and consuntivo. */
export function jobBudget(input) {
    const ecoPlan = new Map();
    const ecoDone = new Map();
    const cashPlan = new Map();
    const cashDone = new Map();
    for (const rata of input.installments) {
        const left = remainder(rata.amount, rata.invoiced);
        add(ecoPlan, rata.issueOn, 'in', left);
        add(cashPlan, rata.collectOn, 'in', left + (rata.splitPayment ? 0 : vatCents(left, rata.vatRate)));
    }
    for (const cost of input.costs) {
        const left = cost.settled ? 0 : remainder(cost.amount, cost.invoiced);
        const withheld = Math.round((left * Number(cost.withholdingRate)) / 100);
        add(ecoPlan, cost.receiveOn, 'out', left);
        add(cashPlan, cost.payOn, 'out', left + vatCents(left, cost.vatRate) - withheld);
    }
    for (const [side, invoices] of [
        ['in', input.issued],
        ['out', input.received],
    ]) {
        for (const invoice of invoices) {
            const amount = toCents(invoice.amount);
            add(ecoPlan, invoice.date, side, amount);
            add(ecoDone, invoice.date, side, amount);
            for (const item of cashMoved(invoice)) {
                const cents = shareOf(invoice, item.cents);
                add(cashPlan, item.date, side, cents);
                add(cashDone, item.date, side, cents);
            }
            for (const item of cashExpected(invoice)) {
                add(cashPlan, item.date, side, shareOf(invoice, item.cents));
            }
        }
    }
    return {
        economico: { previsto: flows(ecoPlan), consuntivo: flows(ecoDone) },
        tesoreria: { previsto: flows(cashPlan), consuntivo: flows(cashDone) },
    };
}
/** The sum of several monthly flows: a madre is the sum of its sottocommesse. */
export function sumFlows(items) {
    const ledger = new Map();
    for (const item of items) {
        item.months.forEach((key, index) => {
            const row = ledger.get(key) ?? { in: 0, out: 0 };
            row.in += toCents(item.in[index]);
            row.out += toCents(item.out[index]);
            ledger.set(key, row);
        });
    }
    return flows(ledger);
}
export function totalOf(values) {
    return fromCents(values.reduce((sum, value) => sum + toCents(value), 0));
}
export function jobExpenses(active, passive) {
    const income = toCents(active);
    const cost = toCents(passive);
    return {
        active: fromCents(income),
        passive: fromCents(cost),
        light: income === 0 && cost === 0 ? null : cost <= income ? 'verde' : 'rosso',
    };
}
const NO_EXPENSES = { active: '0.00', passive: '0.00' };
export function jobTotals(views, thresholds, expenses = NO_EXPENSES) {
    const active = totalOf(views.economico.previsto.in);
    const passive = totalOf(views.economico.previsto.out);
    return {
        active,
        invoiced: totalOf(views.economico.consuntivo.in),
        passive,
        received: totalOf(views.economico.consuntivo.out),
        margin: marginOf(fromCents(toCents(active) - toCents(expenses.active)), fromCents(toCents(passive) - toCents(expenses.passive)), thresholds),
        expenses: jobExpenses(expenses.active, expenses.passive),
    };
}
/** The totals of a madre: the sum of those of its sottocommesse. */
export function sumTotals(items, thresholds) {
    const add = (key) => totalOf(items.map((item) => item[key]));
    const active = add('active');
    const passive = add('passive');
    const expensesActive = totalOf(items.map((item) => item.expenses.active));
    const expensesPassive = totalOf(items.map((item) => item.expenses.passive));
    return {
        active,
        invoiced: add('invoiced'),
        passive,
        received: add('received'),
        margin: marginOf(fromCents(toCents(active) - toCents(expensesActive)), fromCents(toCents(passive) - toCents(expensesPassive)), thresholds),
        expenses: jobExpenses(expensesActive, expensesPassive),
    };
}
export const SNAPSHOT_SOURCE_LABELS = {
    offerta: 'dall’offerta di base',
    avvio: 'all’avvio',
    rilascio: 'al rilascio del budget',
    chiusura: 'alla chiusura',
};
//# sourceMappingURL=job-budget.js.map